import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import './CustomerView.css';

function CustomerView() {
  const { paymentLinkId } = useParams();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'success', 'failed'
  const [razorpayKey, setRazorpayKey] = useState('');
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Fetch order details
  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${config.API_URL}/order/${paymentLinkId}`);
      
      if (response.data.success) {
        setOrder(response.data.order);
        
        // If already paid, show success
        if (response.data.order.status === 'PAID') {
          setPaymentStatus('success');
          setPaymentDetails({
            paidAt: response.data.order.paidAt,
            amount: response.data.order.amount
          });
        }
      } else {
        setError('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      if (error.response?.status === 404) {
        setError('This payment link is invalid or has expired.');
      } else {
        setError('Unable to load payment details. Please try again.');
      }
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  }, [paymentLinkId]);

  // Fetch Razorpay key
  const fetchRazorpayKey = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_URL}/razorpay-key`);
      if (response.data.success) {
        setRazorpayKey(response.data.key);
      }
    } catch (error) {
      console.error('Error fetching Razorpay key:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrderDetails();
    fetchRazorpayKey();
  }, [fetchOrderDetails, fetchRazorpayKey]);

  // Auto redirect after successful payment
  useEffect(() => {
    if (paymentStatus === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus, countdown]);

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      // Check if script already exists
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        console.log('Razorpay script loaded');
        resolve(true);
      };
      script.onerror = () => {
        console.error('Failed to load Razorpay script');
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Handle payment
  const handlePayment = async () => {
    if (!order) return;
    
    setProcessing(true);
    setError(null);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your internet connection.');
        setProcessing(false);
        return;
      }

      // Create Razorpay order on backend
      const response = await axios.post(`${config.API_URL}/create-payment-link`, {
        customerName: order.customerName,
        productName: order.productName,
        amount: order.amount,
        email: order.email || `${order.customerName.toLowerCase().replace(/\s+/g, '')}@example.com`,
        phone: order.phone || '9999999999'
      });

      if (!response.data.success) {
        toast.error(response.data.message || 'Failed to create payment order');
        setProcessing(false);
        return;
      }

      const { razorpayOrderId, razorpayAmount } = response.data;
      const razorpayKeyId = config.RAZORPAY_KEY || razorpayKey;

      if (!razorpayKeyId) {
        toast.error('Payment gateway configuration error. Please contact support.');
        setProcessing(false);
        return;
      }

      // Razorpay checkout options
      const options = {
        key: razorpayKeyId,
        amount: razorpayAmount,
        currency: 'INR',
        name: 'Payment System',
        description: `Payment for ${order.productName}`,
        image: '/logo.png', // Add your logo
        order_id: razorpayOrderId,
        handler: async function (response) {
          // Payment successful, verify on backend
          await verifyPayment(response);
        },
        prefill: {
          name: order.customerName,
          email: order.email || `${order.customerName.toLowerCase().replace(/\s+/g, '')}@example.com`,
          contact: order.phone || '9999999999'
        },
        notes: {
          orderId: order.id,
          paymentLinkId: paymentLinkId,
          customerName: order.customerName,
          productName: order.productName
        },
        theme: {
          color: '#667eea',
          hide_topbar: false
        },
        modal: {
          ondismiss: function () {
            console.log('Payment popup closed');
            setProcessing(false);
            toast.info('Payment cancelled', {
              position: "top-center"
            });
          },
          escape: true,
          animation: true
        },
        retry: {
          enabled: false
        },
        timeout: 300 // 5 minutes timeout
      };

      // Initialize Razorpay
      const razorpay = new window.Razorpay(options);
      
      // Handle payment failure
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        setPaymentStatus('failed');
        setPaymentDetails({
          errorCode: response.error.code,
          errorDescription: response.error.description,
          errorSource: response.error.source,
          errorStep: response.error.step,
          errorReason: response.error.reason
        });
        toast.error(`Payment failed: ${response.error.description}`, {
          position: "top-center",
          autoClose: 5000
        });
        setProcessing(false);
      });

      // Open Razorpay checkout
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      toast.error('An error occurred while processing payment. Please try again.');
      setProcessing(false);
    }
  };

  // Verify payment on backend
  const verifyPayment = async (razorpayResponse) => {
    try {
      const verifyResponse = await axios.post(`${config.API_URL}/verify-payment`, {
        razorpayOrderId: razorpayResponse.razorpay_order_id,
        razorpayPaymentId: razorpayResponse.razorpay_payment_id,
        razorpaySignature: razorpayResponse.razorpay_signature,
        paymentLinkId: paymentLinkId
      });

      if (verifyResponse.data.success) {
        setPaymentStatus('success');
        setPaymentDetails({
          paymentId: razorpayResponse.razorpay_payment_id,
          orderId: razorpayResponse.razorpay_order_id,
          paidAt: verifyResponse.data.order.paidAt || new Date().toISOString(),
          amount: order.amount
        });
        setCountdown(10); // Auto redirect after 10 seconds
        toast.success('✅ Payment successful! Thank you for your payment.', {
          position: "top-center",
          autoClose: 5000
        });
      } else {
        setPaymentStatus('failed');
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setPaymentStatus('failed');
      toast.error('Payment verification failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Retry payment
  const handleRetry = () => {
    setPaymentStatus(null);
    setPaymentDetails(null);
    setError(null);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return new Date(dateString).toLocaleString('en-IN', options);
  };

  // Loading state
  if (loading) {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <h3>Loading Payment Details</h3>
            <p>Please wait while we fetch your order details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (invalid link)
  if (error && !order) {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div className="error-state">
            <div className="error-icon">🔗</div>
            <h2>Invalid Payment Link</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/')} className="btn-home">
                Go to Home
              </button>
              <button onClick={fetchOrderDetails} className="btn-retry">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentStatus === 'success') {
    return (
      <div className="customer-container">
        <div className="payment-card success-animate">
          <div className="success-state">
            <div className="success-animation">
              <div className="success-circle">
                <span className="success-icon-large">✓</span>
              </div>
            </div>
            <h2>Payment Successful!</h2>
            <p className="success-subtitle">Thank you for your payment</p>
            
            <div className="success-details">
              <div className="detail-row">
                <span className="detail-label">Amount Paid</span>
                <span className="detail-value highlight">
                  {formatCurrency(paymentDetails?.amount || order.amount)}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Product/Service</span>
                <span className="detail-value">{order.productName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer Name</span>
                <span className="detail-value">{order.customerName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Payment ID</span>
                <span className="detail-value small">{paymentDetails?.paymentId || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Paid At</span>
                <span className="detail-value">{formatDate(paymentDetails?.paidAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className="status-badge success">PAID</span>
              </div>
            </div>

            {countdown > 0 && (
              <p className="redirect-message">
                Redirecting to home in {countdown} seconds...
              </p>
            )}

            <div className="success-actions">
              <button onClick={() => navigate('/')} className="btn-primary">
                🏪 Visit Store
              </button>
              <button 
                onClick={() => window.print()} 
                className="btn-secondary"
              >
                🖨️ Print Receipt
              </button>
            </div>

            <div className="receipt-note">
              <p>📧 A payment confirmation has been sent to your registered contact.</p>
              <p>For any queries, please contact our support team.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (paymentStatus === 'failed') {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div className="failed-state">
            <div className="failed-icon">❌</div>
            <h2>Payment Failed</h2>
            <p className="failed-message">
              {paymentDetails?.errorDescription || 'Your payment could not be processed.'}
            </p>

            {paymentDetails && (
              <div className="error-details">
                <div className="detail-row">
                  <span className="detail-label">Error Code</span>
                  <span className="detail-value">{paymentDetails.errorCode}</span>
                </div>
                {paymentDetails.errorReason && (
                  <div className="detail-row">
                    <span className="detail-label">Reason</span>
                    <span className="detail-value">{paymentDetails.errorReason}</span>
                  </div>
                )}
              </div>
            )}

            <div className="failed-actions">
              <button onClick={handleRetry} className="btn-primary">
                🔄 Try Again
              </button>
              <button onClick={() => navigate('/')} className="btn-secondary">
                Cancel
              </button>
            </div>

            <div className="help-text">
              <p>💡 Tips for successful payment:</p>
              <ul>
                <li>Ensure you have sufficient balance</li>
                <li>Check your card details are correct</li>
                <li>Try a different payment method</li>
                <li>Contact your bank if issue persists</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment form view
  return (
    <div className="customer-container">
      <div className="payment-card">
        <div className="payment-form-view">
          {/* Merchant Header */}
          <div className="merchant-header">
            <div className="merchant-icon">🏪</div>
            <h2>Complete Your Payment</h2>
            <p className="merchant-subtitle">Secure payment powered by Razorpay</p>
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h3>📋 Order Summary</h3>
            <div className="summary-row">
              <span className="summary-label">Customer Name</span>
              <span className="summary-value">{order.customerName}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Product/Service</span>
              <span className="summary-value">{order.productName}</span>
            </div>
            <div className="summary-row total">
              <span className="summary-label">Total Amount</span>
              <span className="summary-value amount">{formatCurrency(order.amount)}</span>
            </div>
          </div>

          {/* Payment Methods Info */}
          <div className="payment-methods-info">
            <h3>💳 Accepted Payment Methods</h3>
            <div className="payment-methods-grid">
              <div className="payment-method-item">
                <span className="method-icon">💳</span>
                <span>Credit/Debit Cards</span>
              </div>
              <div className="payment-method-item">
                <span className="method-icon">🏦</span>
                <span>Net Banking</span>
              </div>
              <div className="payment-method-item">
                <span className="method-icon">📱</span>
                <span>UPI</span>
              </div>
              <div className="payment-method-item">
                <span className="method-icon">💰</span>
                <span>Wallets</span>
              </div>
            </div>
          </div>

          {/* Pay Button */}
          <button
            onClick={handlePayment}
            disabled={processing}
            className="pay-button"
          >
            {processing ? (
              <>
                <span className="button-spinner"></span>
                <span>Processing Payment...</span>
              </>
            ) : (
              <>
                <span className="pay-icon">🔒</span>
                <span>Pay {formatCurrency(order.amount)}</span>
              </>
            )}
          </button>

          {/* Trust Badges */}
          <div className="trust-badges">
            <div className="secure-badge">
              <span>🔒</span>
              <span>256-bit SSL Encryption</span>
            </div>
            <div className="secure-badge">
              <span>✅</span>
              <span>PCI DSS Compliant</span>
            </div>
            <div className="secure-badge">
              <span>🛡️</span>
              <span>Razorpay Protected</span>
            </div>
          </div>

          {/* Footer */}
          <div className="payment-footer">
            <p>By proceeding, you agree to our Terms of Service and Privacy Policy.</p>
            <p className="support-text">
              Need help? Contact support@paymentsystem.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerView;